import { useState } from 'react';
import VoiceMicButton from '@/components/VoiceMicButton';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { FormPromptDialog } from '@/components/ui/TextPromptDialog';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useEncounterMutation } from '../hooks';
import { handoverBelongings, recordBelongings, recordConsent } from '../api';
import { Section, errorInfo, timeLabel } from '../ui';
import type { ConsentType, Workspace } from '../types';

const CONSENT_LABEL: Record<ConsentType, string> = {
  implied_emergency: 'Implied (emergency) consent',
  informed: 'Informed consent',
  refusal: 'Refusal of treatment',
  lama: 'LAMA / DAMA',
  blood: 'Blood transfusion',
  procedure: 'Procedure',
};

/**
 * Belongings (D18) — inventoried with a witness, required in practice for
 * unconscious and MLC patients, and handed over (to family or police) later,
 * so writes here continue after departure.
 *
 * Consent (D12) — implied emergency consent is recorded WITH its reason;
 * every other type names who gave or refused it. A signed form, when there is
 * one, is uploaded to the patient's documents as usual.
 */
export default function BelongingsConsentSection({ ws }: { ws: Workspace }) {
  const { user } = useAuth();
  const id = ws.encounter.id;
  const canWrite = !!user?.modules?.includes('er:clinical');
  const [items, setItems] = useState('');
  // Hand-over asks in one dialog — window.prompt() is refused by embedded browsers.
  const [handingOver, setHandingOver] = useState<{ bid: number; police: boolean } | null>(null);
  const [w1, setW1] = useState(''); const [w2, setW2] = useState(''); const [loc, setLoc] = useState('');
  const [consent, setConsent] = useState<{ consent_type: ConsentType; given_by: string; relationship: string; scope: string; witness: string; notes: string; risks_explained: boolean; patient_has_capacity: boolean }>(
    { consent_type: 'implied_emergency', given_by: '', relationship: '', scope: '', witness: '', notes: '', risks_explained: false, patient_has_capacity: false });

  const addBelongings = useEncounterMutation(id, (input: Parameters<typeof recordBelongings>[1]) => recordBelongings(id, input));
  const handover = useEncounterMutation(id, (v: { bid: number; body: Parameters<typeof handoverBelongings>[1] }) => handoverBelongings(v.bid, v.body));
  const addConsent = useEncounterMutation(id, (input: Parameters<typeof recordConsent>[1]) => recordConsent(id, input));
  const run = (p: Promise<unknown>, ok: string) => p.then(() => toast({ title: ok, variant: 'success' })).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }));

  const saveBelongings = () => {
    const list = items.split('\n').map((s) => s.trim()).filter(Boolean).map((s) => {
      const m = s.match(/^(\d+)\s*[x×]\s*(.+)$/i);
      return m ? { description: m[2]!, quantity: Number(m[1]) } : { description: s };
    });
    run(addBelongings.mutateAsync({ items: list, witness_1: w1, witness_2: w2 || undefined, storage_location: loc || undefined })
      .then(() => { setItems(''); setW1(''); setW2(''); setLoc(''); }), 'Belongings recorded');
  };

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Section title="Belongings & valuables">
        {canWrite && (
          <div className="mb-3 space-y-2">
            <FormField label="Items (one per line; '2 x Gold ring' for quantity)">
              <div className="flex items-start gap-1">
                <Textarea rows={3} className="flex-1" value={items} onChange={(e) => setItems(e.target.value)} placeholder={'Mobile phone (Samsung, cracked)\nWallet — ₹1,250 cash\n2 x Gold ring'} />
                <VoiceMicButton onText={(t) => setItems((cur) => (cur ? `${cur}\n${t}` : t))} title="Dictate items" />
              </div>
            </FormField>
            <div className="grid grid-cols-3 gap-2">
              <FormField label="Witness 1" required><Input value={w1} onChange={(e) => setW1(e.target.value)} /></FormField>
              <FormField label="Witness 2"><Input value={w2} onChange={(e) => setW2(e.target.value)} /></FormField>
              <FormField label="Stored at"><Input value={loc} onChange={(e) => setLoc(e.target.value)} /></FormField>
            </div>
            <div className="flex justify-end"><Button size="sm" disabled={!items.trim() || !w1.trim()} loading={addBelongings.isPending} onClick={saveBelongings}>Record belongings</Button></div>
          </div>
        )}
        {ws.belongings.length === 0 ? <div className="text-[13px] text-ink-muted">None recorded.</div> : ws.belongings.map((b) => (
          <div key={b.id} className="mb-2 rounded-lg border border-border p-2.5 text-[12px]">
            <div className="flex items-center gap-2">
              <Badge variant={b.status === 'held' ? 'warn' : 'success'}>{b.status === 'held' ? 'held' : b.status === 'police' ? 'handed to police' : 'handed over'}</Badge>
              <span className="ml-auto text-ink-muted">{timeLabel(b.recorded_at)} · {b.recorded_by}</span>
            </div>
            <ul className="ml-4 mt-1 list-disc">{b.items.map((i, k) => <li key={k}>{i.quantity > 1 ? `${i.quantity} × ` : ''}{i.description}{i.remarks ? ` (${i.remarks})` : ''}</li>)}</ul>
            <div className="text-ink-muted">Witnessed by {b.witness_1}{b.witness_2 ? ` and ${b.witness_2}` : ''}{b.storage_location ? ` · stored at ${b.storage_location}` : ''}</div>
            {b.handed_over_to && <div>Handed to {b.handed_over_to}{b.handed_over_relationship ? ` (${b.handed_over_relationship})` : ''} at {timeLabel(b.handed_over_at)}</div>}
            {canWrite && b.status === 'held' && (
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { handover.reset(); setHandingOver({ bid: b.id, police: false }); }}>Hand over to family</Button>
                <Button size="sm" variant="ghost" onClick={() => { handover.reset(); setHandingOver({ bid: b.id, police: true }); }}>Hand to police</Button>
              </div>
            )}
          </div>
        ))}
      </Section>

      <Section title="Consent">
        {canWrite && (
          <div className="mb-3 grid grid-cols-2 gap-2">
            <FormField label="Type" className="col-span-2">
              <NativeSelect value={consent.consent_type} onChange={(e) => setConsent({ ...consent, consent_type: e.target.value as ConsentType })}>
                {(Object.keys(CONSENT_LABEL) as ConsentType[]).map((t) => <option key={t} value={t}>{CONSENT_LABEL[t]}</option>)}
              </NativeSelect>
            </FormField>
            {consent.consent_type !== 'implied_emergency' && (
              <>
                <FormField label="Given / refused by" required><Input value={consent.given_by} onChange={(e) => setConsent({ ...consent, given_by: e.target.value })} /></FormField>
                <FormField label="Relationship"><Input value={consent.relationship} onChange={(e) => setConsent({ ...consent, relationship: e.target.value })} placeholder="Self / spouse / …" /></FormField>
                <FormField label="For (scope)" className="col-span-2"><Input value={consent.scope} onChange={(e) => setConsent({ ...consent, scope: e.target.value })} /></FormField>
                <FormField label="Witness"><Input value={consent.witness} onChange={(e) => setConsent({ ...consent, witness: e.target.value })} /></FormField>
                <label className="flex items-center gap-2 self-end text-[12px]"><Checkbox checked={consent.risks_explained} onCheckedChange={(v) => setConsent({ ...consent, risks_explained: v === true })} /> Risks explained</label>
              </>
            )}
            <FormField label={consent.consent_type === 'implied_emergency' ? 'Why consent is implied (required)' : 'Notes'} className="col-span-2">
              <Textarea rows={2} value={consent.notes} onChange={(e) => setConsent({ ...consent, notes: e.target.value })}
                placeholder={consent.consent_type === 'implied_emergency' ? 'e.g. GCS 6, no relative present, life-saving treatment' : ''} />
            </FormField>
            <div className="col-span-2 flex justify-end">
              <Button size="sm" loading={addConsent.isPending} onClick={() => run(addConsent.mutateAsync({
                consent_type: consent.consent_type, given_by: consent.given_by || undefined, relationship: consent.relationship || undefined,
                scope: consent.scope || undefined, witness: consent.witness || undefined, notes: consent.notes || undefined,
                risks_explained: consent.risks_explained,
              }).then(() => setConsent({ ...consent, given_by: '', relationship: '', scope: '', witness: '', notes: '', risks_explained: false })), 'Consent recorded')}>Record consent</Button>
            </div>
          </div>
        )}
        {ws.consents.length === 0 ? <div className="text-[13px] text-ink-muted">None recorded.</div> : (
          <ul className="space-y-1.5 text-[12px]">
            {ws.consents.map((c) => (
              <li key={c.id} className="rounded-lg border border-border p-2">
                <b>{CONSENT_LABEL[c.consent_type]}</b>{c.scope ? ` — ${c.scope}` : ''}
                <div className="text-ink-muted">
                  {c.given_by ? `${c.given_by}${c.relationship ? ` (${c.relationship})` : ''}` : 'Implied'} · {timeLabel(c.recorded_at)} · {c.recorded_by}
                  {c.witness ? ` · witness ${c.witness}` : ''}{c.risks_explained ? ' · risks explained' : ''}
                </div>
                {c.notes && <div>{c.notes}</div>}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <FormPromptDialog
        open={!!handingOver}
        title={handingOver?.police ? 'Hand belongings to police?' : 'Hand belongings over to family?'}
        description="The belongings are marked handed over, with who received them and when. This is part of the medico-legal record."
        fields={handingOver?.police
          ? [{ name: 'to', label: 'Officer name and police station' }]
          : [{ name: 'to', label: 'Handed over to (name)' }, { name: 'rel', label: 'Relationship to the patient', required: false }]}
        confirmLabel="Record handover"
        pending={handover.isPending}
        error={handover.error ? errorInfo(handover.error).message : null}
        onConfirm={({ to = '', rel }) => {
          if (!handingOver) return;
          const body = handingOver.police
            ? { handed_over_to: to, to_police: true }
            : { handed_over_to: to, relationship: rel || undefined };
          handover.mutate({ bid: handingOver.bid, body }, {
            onSuccess: () => {
              setHandingOver(null);
              toast({ title: handingOver.police ? 'Handover to police recorded' : 'Handover recorded', variant: 'success' });
            },
          });
        }}
        onClose={() => setHandingOver(null)}
      />
    </div>
  );
}
